import { useState } from "react";
import dayjs from "dayjs";
import {
  DataProvider,
  getUsers,
  postReminderFormData,
  postSendReminder,
  ReminderCollector,
} from "api";
import { useNotify } from "hooks";
import { extractErrorMessage } from "utils";
import { buildHtmlMessage, DEFAULT_MESSAGE, DEFAULT_SUBJECT } from "../consts";
import { RecipientChip, ReminderFormValues } from "./types";
import { unifyDaysUsage } from "../../month-cell/helpers/unifyDaysUsage";
import { checkDaysMatchMonthLength } from "../../../helpers/checkDaysMatchMonthLength";

const computePartialMonths = (
  row: any,
  fromDate: string,
  toDate: string,
): string[] => {
  if (!row?.months) return [];
  const result: string[] = [];
  let current = dayjs(fromDate).startOf("month");
  const end = dayjs(toDate).startOf("month");
  while (!current.isAfter(end)) {
    const monthKey = current.format("MMM");
    const days = row.months?.[monthKey]?.days ?? [];
    const unifiedDays = unifyDaysUsage(days);
    const hasNullUsage =
      unifiedDays.length > 0 &&
      !checkDaysMatchMonthLength(unifiedDays.length, current.month() + 1, current.year());
    if (hasNullUsage) {
      result.push(`${current.format("MMM")} '${current.format("YY")}`);
    }
    current = current.add(1, "month");
  }
  return result;
};

export const useSendReminder = ({
  collectorIds,
  fromDate,
  toDate,
  selectedRowsData,
}: {
  collectorIds: string[];
  fromDate: string;
  toDate: string;
  selectedRowsData?: any[];
}) => {
  const notify = useNotify();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingFormData, setIsLoadingFormData] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [dataProviders, setDataProviders] = useState<DataProvider[]>([]);
  const [recipients, setRecipients] = useState<RecipientChip[]>([]);
  const [collectors, setCollectors] = useState<ReminderCollector[]>([]);
  const [platformUsers, setPlatformUsers] = useState<RecipientChip[]>([]);

  const openModal = async () => {
    setIsModalOpen(true);
    setIsLoadingFormData(true);
    try {
      const [formData, users] = await Promise.all([
        postReminderFormData({
          collectorIds: collectorIds.map(Number),
          fromDate,
          toDate,
        }),
        getUsers(),
      ]);
      const rowLookup = new Map<string, any>();
      selectedRowsData?.forEach((row) => {
        const account = row.collector?.accountNumber;
        const meter = row.collector?.meterNumber;
        if (account && meter) {
          rowLookup.set(`${account}|${meter}`, row);
        }
      });

      const enrichedCollectors = formData.collectors.map((c) => {
        const row = rowLookup.get(`${c.accountNumber}|${c.meterNumber}`);
        return row
          ? { ...c, partialMonths: computePartialMonths(row, fromDate, toDate) }
          : c;
      });
      setCollectors(enrichedCollectors);
      setDataProviders(formData.dataProviders ?? []);
      setRecipients([]);
      setPlatformUsers(
        users.map((u) => ({
          id: u.id,
          firstName: u.firstName,
          lastName: u.lastName,
          email: u.email,
        })),
      );
    } catch (err) {
      notify.error("Failed to load reminder form data.");
      console.error("Failed to load reminder form data", err);
    } finally {
      setIsLoadingFormData(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setDataProviders([]);
    setRecipients([]);
    setCollectors([]);
    setPlatformUsers([]);
  };

  const handleDataProviderRemove = (userId: number) => {
    setDataProviders((prev) => prev.filter((p) => p.userId !== userId));
  };

  const handleSend = async (formValues: ReminderFormValues) => {
    setIsSending(true);
    try {
      await postSendReminder({
        dataProviderUserIds: dataProviders.map((p) => p.userId),
        recipientUserIds: recipients
          .filter((r) => r.id !== undefined)
          .map((r) => r.id as number),
        subject: formValues.subject,
        message: buildHtmlMessage(
          {
            helloMessage: formValues.helloMessage,
            signatureMessage: formValues.signatureMessage,
            tableDescriptionMessage: undefined,
          },
          collectors,
        ),
      });
      closeModal();
      notify.success("Reminder sent successfully.");
    } catch (err) {
      notify.error(extractErrorMessage(err, "Failed to send reminder."));
      console.error("Failed to send reminder", err);
    } finally {
      setIsSending(false);
    }
  };

  const handleRecipientsChange = (newValue: (RecipientChip | string)[]) => {
    setRecipients(
      newValue.map((v) => {
        if (typeof v === "string") {
          const match = platformUsers.find((u) => u.email === v);
          return match ?? { email: v };
        }
        return v;
      }),
    );
  };

  return {
    isModalOpen,
    isLoadingFormData,
    isSending,
    dataProviders,
    recipients,
    defaultSubject: DEFAULT_SUBJECT,
    defaultMessage: DEFAULT_MESSAGE,
    collectors,
    platformUsers,
    openModal,
    closeModal,
    handleDataProviderRemove,
    handleSend,
    handleRecipientsChange,
  };
};
